import { useEffect, useRef } from 'react';

/**
 * PostHog mit Cookiebot-Consent-Gate (Kategorie 'statistics').
 *
 * WARUM: PostHog stand vorher inline in public/index.html und feuerte sofort beim
 * Laden - inkl. Session-Recording auf EU-Kunden, OHNE Consent. Das war das eigentliche
 * DSGVO-Loch. Ein Cookiebot-Attribut allein reicht nicht, weil ein Inline-Script sofort
 * ausgefuehrt wird. Deshalb: Init erst nach explizitem Consent (statistics), analog zu
 * CrispChat.jsx.
 *
 * PostHog wird ausschliesslich hier verwendet (grep -rn posthog src/ = 0 Treffer),
 * daher hat das Verschieben aus index.html keine Seiteneffekte.
 */

// Public PostHog Project API Key (kein Secret) + Config - identisch zur bisherigen Inline-Init.
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_OPTIONS = {
  api_host: 'https://us.i.posthog.com',
  person_profiles: 'identified_only',
  session_recording: {
    recordCrossOriginIframes: true,
    capturePerformance: false,
  },
};

const hasStatisticsConsent = () => {
  const c = typeof window !== 'undefined' && window.Cookiebot && window.Cookiebot.consent;
  return !!(c && c.statistics);
};

const loadPostHog = () => {
  if (typeof window === 'undefined') return;
  // Doppel-Laden verhindern: nach init existiert window.posthog.__loaded / __SV.
  if (window.posthog && window.posthog.__SV) return;

  // Standard-PostHog-Loader-Stub (unveraendert aus dem bisherigen Inline-Snippet).
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o = 'init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric'.split(
              ' '
            ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */

  window.posthog.init(POSTHOG_KEY, POSTHOG_OPTIONS);
};

export default function PostHogGate() {
  const loadedRef = useRef(false);

  useEffect(() => {
    const applyConsent = () => {
      // Nur bei erteiltem 'statistics'-Consent laden. Bei Decline/kein Consent: nichts tun.
      // (PostHog laesst sich nach Init nicht sauber ohne Reload entladen; das Gate verhindert
      //  den Load beim naechsten Reload, wenn der Consent widerrufen wurde.)
      if (hasStatisticsConsent() && !loadedRef.current) {
        loadPostHog();
        loadedRef.current = true;
      }
    };

    // Falls Cookiebot bereits vor dem React-Mount gefeuert hat.
    applyConsent();

    window.addEventListener('CookiebotOnAccept', applyConsent);
    window.addEventListener('CookiebotOnDecline', applyConsent);
    return () => {
      window.removeEventListener('CookiebotOnAccept', applyConsent);
      window.removeEventListener('CookiebotOnDecline', applyConsent);
    };
  }, []);

  return null;
}
